import { z } from 'zod';
/**
 * Operator entity (B2B customer / IP brand). Each Cognito B2B user
 * gets exactly one operator row (PK = operatorId = Cognito sub).
 *
 * Lifecycle:
 *   - On first sign-in, a post-confirmation Lambda creates the row
 *     with status='pending'.
 *   - Operator fills in company name / tax ID / phone / description
 *     via /account/operator-profile.
 *   - Superadmin reviews + approves → status='approved'. Until
 *     approved, operator can browse but cannot create characters
 *     or receive transfers.
 *   - Superadmin can suspend or delete (out-of-band).
 *
 * Currency:
 *   - currencyName + currencyIconUrl let the operator brand their
 *     in-character wallet (e.g. "星塵 / Stardust"). Fans see this on
 *     their per-operator balance card.
 *   - Defaults to a generic "點" name if unset.
 *
 * Earnings:
 *   - pendingBalance tracks NTD-equivalent earnings not yet
 *     withdrawn. Bumped on every platform→operator transfer (revenue
 *     share × amount). Decremented on payout via admin tool.
 *   - revenueShare is 0..1; defaults to 1.0 (operator gets 100%);
 *     platform can override per-operator via admin tool.
 */
export const OperatorStatusEnum = z.enum(['pending', 'approved', 'suspended']);
export const OperatorProfileSchema = z.object({
    companyName: z.string().min(1).max(100),
    taxId: z.string().max(20).optional(),
    // Only companyName is mandatory per product rule (operator setup
    // shouldn't gate on optional fields). Empty string allowed; backend
    // normalizes empty → undefined at the handler.
    contactPhone: z.string().max(30).optional(),
    description: z.string().max(500).optional().default(''),
});
export const OperatorSchema = z.object({
    operatorId: z.string().min(1).max(128),
    email: z.string().email(),
    status: OperatorStatusEnum,
    profile: OperatorProfileSchema.optional(),
    currencyName: z.string().min(1).max(20).default('點'),
    currencyIconUrl: z.string().url().optional(),
    /** @deprecated Use platformCutBps instead. Kept for back-compat. */
    revenueShare: z.number().min(0).max(1).default(1),
    /**
     * Per `operator-revenue-share` capability — platform's cut in basis
     * points (0..10000). Default 3000 = 30%. Editable by superadmin only
     * via PATCH /api/admin/operators/{id}/cut-bps. Applied at every
     * chat-spend: operator gets `1 - cutBps/10000` share, platform takes
     * the rest. Field-level integer for exact arithmetic — no floats.
     */
    platformCutBps: z.number().int().min(0).max(10000).default(3000),
    pendingBalanceTwd: z.number().min(0).default(0),
    lifetimeEarnedTwd: z.number().min(0).default(0),
    /**
     * Per `nfc-point-cards` — caps on operator-issued point cards, set by
     * superadmin in 經營者審查. `pointCardTotalCap` = max total face value the
     * operator may have issued (reserved) at once; `pointCardPerCardCap` =
     * max face value of a single card; `pointCardReserved` = running Σ face
     * of the operator's active+redeemed cards (released when a card is
     * voided). Admin-issued cards bypass all three. Default 0 = cannot issue
     * until an admin grants a cap.
     */
    pointCardTotalCap: z.number().int().min(0).optional(),
    pointCardPerCardCap: z.number().int().min(0).optional(),
    pointCardReserved: z.number().int().min(0).optional(),
    /**
     * Per `operator-payouts` — operator's preferred bank account for
     * payouts. Set by operator in /account/payouts and reused on every
     * subsequent payout request so they don't re-type. Stored server-
     * side (replaces the localStorage-only persistence from iter 14).
     * Operator can update / clear at will.
     */
    defaultBankAccount: z.object({
        bankCode: z.string().min(1).max(10),
        accountNumber: z.string().min(1).max(30),
        accountHolder: z.string().min(1).max(50),
    }).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    approvedAt: z.string().datetime().optional(),
    approvedBy: z.string().optional(),
});
